/**
 * ProcurementDashboard — manager role dashboard for the Procurement function.
 *
 * Built from /tmp/aura-design/extracted/aura-web-app/project/aura-dash-3.jsx
 * (`ProcurementDashboard`). UI-only: no backend changes. Uses design tokens
 * already shipped into `src/index.css` (Phase 1 of UI rebuild). Every widget
 * handles loading / error / empty independently so a single endpoint outage
 * never blanks the whole page.
 *
 * Sidebar label for this route = "Rate Comparison" per DECISIONS.md D-06.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Filter, Plus, Sparkles, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';

/* ------------------------------------------------------------------ */
/*  Local compact-currency helper                                      */
/*  formatINR() in utils only supports full numbers. Widgets need a    */
/*  compact lakhs/crores form for small cards.                         */
/* ------------------------------------------------------------------ */
function compactINR(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return '\u2014';
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1)} K`;
  return `${sign}₹${Math.round(abs).toLocaleString('en-IN')}`;
}

/* ------------------------------------------------------------------ */
/*  Shared atoms                                                       */
/* ------------------------------------------------------------------ */
function KpiCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'warn' | 'ok';
}) {
  const subColor =
    tone === 'warn'
      ? 'var(--warn)'
      : tone === 'ok'
        ? 'var(--forest-600)'
        : 'var(--ink-3)';
  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          fontWeight: 500,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--ff-serif, serif)',
          fontSize: 26,
          fontWeight: 500,
          color: 'var(--ink-0)',
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 11, color: subColor, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function LoadingPill() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        color: 'var(--ink-3)',
      }}
    >
      <Loader2 size={12} className="animate-spin" /> Loading…
    </span>
  );
}

function ErrorPill({ msg = 'Unavailable' }: { msg?: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        color: 'var(--danger)',
      }}
    >
      <AlertTriangle size={12} /> {msg}
    </span>
  );
}

function EmptyState({ msg }: { msg: string }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: 'var(--ink-3)',
        fontStyle: 'italic',
        padding: 12,
        textAlign: 'center',
      }}
    >
      {msg}
    </div>
  );
}

function Donut({
  size = 180,
  thickness = 28,
  segments,
  label,
}: {
  size?: number;
  thickness?: number;
  segments: { value: number; color: string }[];
  label?: React.ReactNode;
}) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        margin: '0 auto',
      }}
    >
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {total === 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--ivory-2)"
            strokeWidth={thickness}
          />
        )}
        {segments.map((seg, i) => {
          const pct = total > 0 ? seg.value / total : 0;
          const dash = pct * circumference;
          const gap = circumference - dash;
          const circle = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += dash;
          return circle;
        })}
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        {label}
      </div>
    </div>
  );
}

function ProgressBar({
  value,
  color = 'var(--ink-0)',
  h = 4,
  bg = 'var(--ivory-2)',
}: {
  value: number;
  color?: string;
  h?: number;
  bg?: string;
}) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      style={{
        background: bg,
        borderRadius: 999,
        height: h,
        overflow: 'hidden',
        marginTop: 4,
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          borderRadius: 999,
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Data hooks                                                         */
/* ------------------------------------------------------------------ */

type FetchState<T> = { data: T | null; loading: boolean; error: string | null };

function useFetch<T>(url: string | null): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: null,
    loading: !!url,
    error: null,
  });
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    api
      .get(url)
      .then((res) => {
        if (cancelled) return;
        // Most AURA endpoints return { success, data, ... } — unwrap.
        const payload = res.data?.data !== undefined ? res.data.data : res.data;
        setState({ data: payload as T, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err?.response?.status === 404
            ? 'Not available yet'
            : err?.message ?? 'Error';
        setState({ data: null, loading: false, error: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return state;
}

/* ------------------------------------------------------------------ */
/*  Widgets                                                            */
/* ------------------------------------------------------------------ */

interface PORow {
  id: string;
  number: string;
  status: string;
  total?: number | string | null;
  subtotal?: number | string | null;
  vendor?: { name?: string } | null;
}

const PO_COLUMNS: { key: string; label: string; color: string }[] = [
  { key: 'draft', label: 'Draft', color: 'var(--ink-3)' },
  { key: 'sent', label: 'Sent', color: '#C67C1B' },
  { key: 'acknowledged', label: 'Acknowledged', color: 'var(--info)' },
  { key: 'partially_received', label: 'Partially Received', color: 'var(--saffron)' },
  { key: 'received', label: 'Received', color: 'var(--forest)' },
  { key: 'closed', label: 'Closed', color: 'var(--ink-3)' },
];

function POKanbanStrip() {
  const q = useFetch<{ data?: PORow[] } | PORow[]>(
    '/purchase-orders?limit=60'
  );

  const grouped = useMemo(() => {
    const rows: PORow[] = Array.isArray(q.data)
      ? q.data
      : Array.isArray((q.data as { data?: PORow[] } | null)?.data)
        ? ((q.data as { data?: PORow[] }).data as PORow[])
        : [];
    const by: Record<string, PORow[]> = {};
    for (const col of PO_COLUMNS) by[col.key] = [];
    for (const po of rows) {
      const k = (po.status ?? 'draft').toLowerCase();
      if (by[k]) by[k].push(po);
    }
    // Cap each col to 5 so the strip stays tidy.
    for (const col of PO_COLUMNS) by[col.key] = by[col.key].slice(0, 5);
    return by;
  }, [q.data]);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            fontWeight: 500,
          }}
        >
          PO pipeline
        </div>
        <div style={{ flex: 1 }} />
        {q.loading && <LoadingPill />}
        {q.error && <ErrorPill />}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, minmax(160px, 1fr))',
          gap: 8,
          overflowX: 'auto',
        }}
      >
        {PO_COLUMNS.map((col) => {
          const items = grouped[col.key] ?? [];
          return (
            <div
              key={col.key}
              style={{
                background: 'var(--ivory-1)',
                borderRadius: 10,
                border: '1px solid var(--line)',
                minHeight: 220,
                padding: 8,
              }}
            >
              <div
                style={{
                  padding: '4px 6px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    background: col.color,
                  }}
                />
                <span style={{ fontSize: 12, fontWeight: 500 }}>
                  {col.label}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    color: 'var(--ink-3)',
                    marginLeft: 'auto',
                  }}
                >
                  {items.length}
                </span>
              </div>
              {!q.loading && items.length === 0 && (
                <EmptyState msg="None" />
              )}
              {items.map((it) => {
                const amount = Number(it.total ?? it.subtotal ?? 0);
                return (
                  <Link
                    key={it.id}
                    to={`/purchase-orders/${it.id}`}
                    style={{
                      display: 'block',
                      background: 'var(--ivory-0)',
                      border: '1px solid var(--ivory-3)',
                      borderRadius: 6,
                      padding: 8,
                      marginBottom: 6,
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'var(--ff-mono, monospace)',
                        fontSize: 10,
                        color: 'var(--ink-3)',
                        marginBottom: 2,
                      }}
                    >
                      {it.number}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        marginBottom: 4,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {it.vendor?.name ?? '\u2014'}
                    </div>
                    <div style={{ fontSize: 13 }}>{compactINR(amount)}</div>
                  </Link>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface SoPoStats {
  averageMappingTimeHours: number;
  unmappedCount: number;
  inProgressCount: number;
  mappedCount: number;
  overdueCount: number;
  totalItems: number;
}

function SoPoDonutCard() {
  const q = useFetch<SoPoStats>('/so-po-mapping/kpis');

  const segments = useMemo(() => {
    const d = q.data;
    if (!d) return [] as { value: number; color: string }[];
    // Green = mapped, Yellow = in_progress, Red = overdue unmapped
    return [
      { value: d.mappedCount, color: 'var(--forest)' },
      { value: d.inProgressCount, color: '#C67C1B' },
      { value: d.overdueCount, color: 'var(--danger)' },
    ];
  }, [q.data]);

  const total = q.data
    ? q.data.mappedCount + q.data.inProgressCount + q.data.overdueCount
    : 0;
  const mappedPct =
    total > 0 && q.data
      ? Math.round((q.data.mappedCount / total) * 100)
      : 0;

  return (
    <Link
      to="/so-po-mapping"
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <div
        style={{
          background: 'var(--ivory-1)',
          border: '1px solid var(--line)',
          borderRadius: 10,
          padding: 16,
          cursor: 'pointer',
          transition: 'border-color 0.15s',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.06,
                fontWeight: 500,
              }}
            >
              SO → PO mapping
            </div>
            <div
              style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}
            >
              Time from sales order to purchase order
            </div>
          </div>
          <div style={{ flex: 1 }} />
          {q.loading && <LoadingPill />}
          {q.error && <ErrorPill />}
        </div>
        {q.data && total === 0 && (
          <EmptyState msg="No mapping data yet" />
        )}
        {q.data && total > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '180px 1fr',
              gap: 20,
            }}
          >
            <Donut
              size={160}
              thickness={24}
              segments={segments}
              label={
                <>
                  <div
                    style={{
                      fontFamily: 'var(--ff-serif, serif)',
                      fontSize: 30,
                      fontWeight: 500,
                    }}
                  >
                    {mappedPct}%
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    mapped on time
                  </div>
                </>
              }
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              {[
                {
                  k: 'Green',
                  d: '< 24 hours',
                  c: 'var(--forest)',
                  v: q.data.mappedCount,
                },
                {
                  k: 'Yellow',
                  d: '24 – 48 hours',
                  c: '#C67C1B',
                  v: q.data.inProgressCount,
                },
                {
                  k: 'Red',
                  d: '> 48 hours',
                  c: 'var(--danger)',
                  v: q.data.overdueCount,
                },
              ].map((r) => {
                const pct = total > 0 ? (r.v / total) * 100 : 0;
                return (
                  <div
                    key={r.k}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 6,
                        background: r.c,
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {r.k} · {r.d}
                      </div>
                      <ProgressBar value={pct} color={r.c} h={4} />
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--ff-serif, serif)',
                        fontSize: 18,
                        fontWeight: 500,
                      }}
                    >
                      {r.v}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}

/* ---------- Vendor scorecard table ---------- */
interface VendorRow {
  id?: string;
  name: string;
  onTimePct?: number | null;
  rejectPct?: number | null;
  avgLeadDays?: number | null;
  rating?: number | null;
  poCount?: number | null;
}

function VendorScorecardTable() {
  // Primary endpoint: role-KPIs. Fallback: vendors?includeKPIs=1.
  const q1 = useFetch<VendorRow[] | { data: VendorRow[] }>(
    '/role-kpis/procurement/vendor-scorecard'
  );
  const q2Url =
    q1.error && !q1.loading ? '/vendors?includeKPIs=1&limit=10' : null;
  const q2 = useFetch<VendorRow[] | { data: VendorRow[] }>(q2Url);

  const rows: VendorRow[] = useMemo(() => {
    const raw =
      (q1.data as { data?: VendorRow[] } | VendorRow[] | null) ??
      (q2.data as { data?: VendorRow[] } | VendorRow[] | null);
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : raw.data;
    return (list ?? []).slice(0, 10);
  }, [q1.data, q2.data]);

  const loading = q1.loading || q2.loading;
  const error = !loading && rows.length === 0 && (q1.error || q2.error);

  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            fontWeight: 500,
          }}
        >
          Vendor scorecard
        </div>
        <div style={{ flex: 1 }} />
        {loading && <LoadingPill />}
        {error && <ErrorPill msg="Not available yet" />}
      </div>
      {!loading && rows.length === 0 && !error && (
        <EmptyState msg="No vendor data yet" />
      )}
      {rows.length > 0 && (
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: 'var(--ink-3)', fontSize: 11 }}>
              <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: 500 }}>
                Vendor
              </th>
              <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 500 }}>
                POs
              </th>
              <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 500 }}>
                On-time
              </th>
              <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 500 }}>
                Reject
              </th>
              <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 500 }}>
                Lead
              </th>
              <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 500 }}>
                Rating
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const ot = Number(r.onTimePct ?? 0);
              return (
                <tr
                  key={r.id ?? i}
                  style={{ borderTop: '1px solid var(--ivory-3)' }}
                >
                  <td
                    style={{
                      fontWeight: 500,
                      padding: '8px 4px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 180,
                    }}
                  >
                    {r.name}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 4px' }}>
                    {r.poCount ?? '\u2014'}
                  </td>
                  <td
                    style={{
                      textAlign: 'right',
                      padding: '8px 4px',
                      color:
                        ot >= 90
                          ? 'var(--forest-600)'
                          : ot >= 80
                            ? 'var(--ink-1)'
                            : 'var(--danger)',
                    }}
                  >
                    {r.onTimePct != null ? `${Math.round(ot)}%` : '\u2014'}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 4px' }}>
                    {r.rejectPct != null
                      ? `${Math.round(Number(r.rejectPct))}%`
                      : '\u2014'}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 4px' }}>
                    {r.avgLeadDays != null
                      ? `${Math.round(Number(r.avgLeadDays))}d`
                      : '\u2014'}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 4px' }}>
                    {r.rating != null ? (
                      <span style={{ color: 'var(--saffron-600)' }}>
                        ★ {Number(r.rating).toFixed(1)}
                      </span>
                    ) : (
                      '\u2014'
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ---------- Split-award summary ---------- */
interface SplitAwardSummary {
  multiVendorPOsThisMonth: number;
  avgSavingsPct: number | null;
  totalSavings: number | null;
}

function SplitAwardSummaryCard() {
  const q = useFetch<SplitAwardSummary>(
    '/role-kpis/procurement/split-award-summary'
  );
  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          fontWeight: 500,
          marginBottom: 8,
        }}
      >
        Split-award summary · MTD
      </div>
      {q.loading && <LoadingPill />}
      {q.error && <EmptyState msg="No split-award data yet" />}
      {q.data && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Multi-vendor</div>
            <div
              style={{
                fontFamily: 'var(--ff-serif, serif)',
                fontSize: 22,
                fontWeight: 500,
              }}
            >
              {q.data.multiVendorPOsThisMonth ?? 0}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Avg savings</div>
            <div
              style={{
                fontFamily: 'var(--ff-serif, serif)',
                fontSize: 22,
                fontWeight: 500,
                color: 'var(--forest-600)',
              }}
            >
              {q.data.avgSavingsPct != null
                ? `${q.data.avgSavingsPct.toFixed(1)}%`
                : '\u2014'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Saved</div>
            <div
              style={{
                fontFamily: 'var(--ff-serif, serif)',
                fontSize: 22,
                fontWeight: 500,
                color: 'var(--forest-600)',
              }}
            >
              {q.data.totalSavings != null
                ? compactINR(q.data.totalSavings)
                : '\u2014'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Landed cost preview ---------- */
interface LandedCostPreview {
  quotationJobId?: string;
  rfqNumber?: string;
  itemLabel?: string;
  landedCost?: number;
  targetPrice?: number;
  totalValue?: number;
}

function LandedCostPreviewCard() {
  const q = useFetch<LandedCostPreview>(
    '/quotation-jobs/latest-with-landed-cost'
  );
  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          fontWeight: 500,
          marginBottom: 8,
        }}
      >
        Landed-cost preview
      </div>
      {q.loading && <LoadingPill />}
      {(q.error || !q.data || !q.data.landedCost) && !q.loading && (
        <EmptyState msg="No recent quotation with landed cost" />
      )}
      {q.data && q.data.landedCost != null && (
        <>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>
            {q.data.rfqNumber ?? '\u2014'}
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              marginBottom: 10,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {q.data.itemLabel ?? '\u2014'}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Landed</div>
              <div
                style={{
                  fontFamily: 'var(--ff-serif, serif)',
                  fontSize: 18,
                  fontWeight: 500,
                }}
              >
                {compactINR(q.data.landedCost)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Target</div>
              <div style={{ fontSize: 14, color: 'var(--ink-2)' }}>
                {q.data.targetPrice != null
                  ? compactINR(q.data.targetPrice)
                  : '\u2014'}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Quotation jobs pending ---------- */
interface QuotationJob {
  id: string;
  number?: string;
  title?: string;
  itemDescription?: string;
  status: string;
}

function QuotationJobsPendingCard() {
  const q = useFetch<
    | { data: QuotationJob[]; pagination?: unknown }
    | QuotationJob[]
  >('/quotation-jobs?status=awaiting_award&limit=10');

  const jobs: QuotationJob[] = useMemo(() => {
    if (!q.data) return [];
    return Array.isArray(q.data) ? q.data : q.data.data ?? [];
  }, [q.data]);

  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            fontWeight: 500,
          }}
        >
          RFQs awaiting award
        </div>
        <div style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: jobs.length > 0 ? 'var(--warn)' : 'var(--ink-3)',
            background: jobs.length > 0 ? 'var(--warn-bg)' : 'var(--ivory-2)',
            padding: '2px 8px',
            borderRadius: 999,
          }}
        >
          {q.loading ? '…' : jobs.length}
        </span>
      </div>
      {q.loading && <LoadingPill />}
      {q.error && <ErrorPill msg="Unavailable" />}
      {!q.loading && !q.error && jobs.length === 0 && (
        <EmptyState msg="No RFQs awaiting award" />
      )}
      {jobs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {jobs.slice(0, 6).map((j) => (
            <Link
              key={j.id}
              to={`/quotation-jobs/${j.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 6,
                background: 'var(--ivory-0)',
                border: '1px solid var(--ivory-3)',
                fontSize: 12,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--ff-mono, monospace)',
                  fontSize: 10,
                  color: 'var(--ink-3)',
                  width: 70,
                  flexShrink: 0,
                }}
              >
                {j.number ?? j.id.slice(0, 6)}
              </span>
              <span
                style={{
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {j.title ?? j.itemDescription ?? '\u2014'}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Rate comparison mini-chart ---------- */
interface RateComparisonSummary {
  commodity: string;
  avgRateDeltaPct: number;
}

function RateComparisonMini() {
  const q = useFetch<RateComparisonSummary[] | { data: RateComparisonSummary[] }>(
    '/vendor-rate-comparison/summary'
  );
  const rows: RateComparisonSummary[] = useMemo(() => {
    if (!q.data) return [];
    return Array.isArray(q.data) ? q.data : q.data.data ?? [];
  }, [q.data]);

  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          fontWeight: 500,
          marginBottom: 10,
        }}
      >
        Rate change · Top 5 commodities
      </div>
      {q.loading && <LoadingPill />}
      {q.error && <EmptyState msg="Not available yet" />}
      {!q.loading && !q.error && rows.length === 0 && (
        <EmptyState msg="No rate data yet" />
      )}
      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.slice(0, 5).map((r, i) => {
            const pct = Number(r.avgRateDeltaPct);
            const positive = pct > 0;
            const absPct = Math.min(Math.abs(pct), 20);
            return (
              <div
                key={i}
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}
              >
                <div
                  style={{
                    width: 100,
                    fontSize: 11,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {r.commodity}
                </div>
                <div style={{ flex: 1, position: 'relative', height: 10 }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: 0,
                      bottom: 0,
                      width: 1,
                      background: 'var(--line)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 2,
                      bottom: 2,
                      left: positive ? '50%' : `${50 - absPct * 2.5}%`,
                      width: `${absPct * 2.5}%`,
                      background: positive
                        ? 'var(--danger)'
                        : 'var(--forest)',
                      borderRadius: 3,
                    }}
                  />
                </div>
                <div
                  style={{
                    width: 60,
                    textAlign: 'right',
                    fontSize: 12,
                    fontWeight: 500,
                    color: positive ? 'var(--danger)' : 'var(--forest-600)',
                  }}
                >
                  {positive ? '+' : ''}
                  {pct.toFixed(1)}%
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- AI CTA card ---------- */
function AiSuggestSplitCard() {
  return (
    <Link
      to="/ai?context=procurement&action=split-suggest"
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        background:
          'linear-gradient(135deg, var(--saffron-50) 0%, var(--ivory-1) 100%)',
        border: '1px solid var(--saffron-100)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <Sparkles size={14} style={{ color: 'var(--saffron-600)' }} />
        <span
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            fontWeight: 500,
            color: 'var(--saffron-600)',
          }}
        >
          AI · 5 credits
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
        Suggest optimal split awards
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
        AI analyses vendor rates, lead time and ratings to recommend
        multi-vendor splits for pending RFQs.
      </div>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function ProcurementDashboard() {
  // Top-level KPIs derived from the SO-PO stats + PO list we already fetch
  // for the strip + donut. To keep this page a pure composition of widgets,
  // we fetch them here too — Browser HTTP cache will dedupe.
  const poStats = useFetch<SoPoStats>('/so-po-mapping/kpis');
  const openPos = useFetch<{
    value?: number;
    meta?: { committed?: number };
  }>('/role-kpis/procurement/open-pos');
  const pending = useFetch<{ value?: number; meta?: { amount?: number } }>(
    '/role-kpis/procurement/rfqs-awaiting-award'
  );
  const onTime = useFetch<{ value?: number }>(
    '/role-kpis/procurement/vendor-overdue'
  );

  const kpis = [
    {
      l: 'Open POs',
      v:
        openPos.loading
          ? '…'
          : openPos.data?.value != null
            ? String(openPos.data.value)
            : '\u2014',
      s:
        openPos.data?.meta?.committed != null
          ? `${compactINR(openPos.data.meta.committed)} committed`
          : 'Currently open',
    },
    {
      l: 'Pending approvals',
      v:
        pending.loading
          ? '…'
          : pending.data?.value != null
            ? String(pending.data.value)
            : '\u2014',
      s:
        pending.data?.meta?.amount != null
          ? `${compactINR(pending.data.meta.amount)} awaiting sign-off`
          : 'RFQs awaiting award',
      tone: 'warn' as const,
    },
    {
      l: 'Avg mapping time',
      v:
        poStats.loading
          ? '…'
          : poStats.data?.averageMappingTimeHours != null
            ? `${poStats.data.averageMappingTimeHours}h`
            : '\u2014',
      s: 'SO → PO · target <24h',
      tone:
        poStats.data && poStats.data.averageMappingTimeHours > 24
          ? ('warn' as const)
          : undefined,
    },
    {
      l: 'Overdue vendors',
      v:
        onTime.loading
          ? '…'
          : onTime.data?.value != null
            ? String(onTime.data.value)
            : '\u2014',
      s: 'POs past delivery date',
      tone: 'warn' as const,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div
        style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}
      >
        <div>
          <h2
            style={{
              fontFamily: 'var(--ff-serif, serif)',
              fontSize: 22,
              fontWeight: 500,
              margin: 0,
            }}
          >
            Procurement
          </h2>
          <div
            style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}
          >
            Rate comparison · vendor scorecards · PO pipeline
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {/* Apr 27 (audit P1) — Filter scrolls to vendor scorecard table + opens
            its filter chip (inline; lightweight). */}
        <button
          onClick={() => {
            const target = document.querySelector('[data-procurement-section="vendor-scorecard"]')
              ?? document.querySelector('[data-procurement-section]');
            (target as HTMLElement | null)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'transparent',
            border: '1px solid var(--line)',
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--ink-1)',
            cursor: 'pointer',
          }}
        >
          <Filter size={13} /> Filter
        </button>
        <Link
          to="/purchase-orders/new"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginLeft: 8,
            background: 'var(--ink-0)',
            color: 'var(--ivory-0)',
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            textDecoration: 'none',
          }}
        >
          <Plus size={13} /> New PO
        </Link>
      </div>

      {/* KPIs */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginBottom: 16,
        }}
      >
        {kpis.map((k, i) => (
          <KpiCard key={i} label={k.l} value={k.v} sub={k.s} tone={k.tone} />
        ))}
      </div>

      {/* PO pipeline kanban */}
      <div style={{ marginBottom: 16 }}>
        <POKanbanStrip />
      </div>

      {/* SO-PO donut */}
      <div style={{ marginBottom: 16 }}>
        <SoPoDonutCard />
      </div>

      {/* Second row: vendor scorecard + split-award summary */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '3fr 2fr',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <VendorScorecardTable />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <SplitAwardSummaryCard />
          <LandedCostPreviewCard />
        </div>
      </div>

      {/* Third row: quotation jobs pending + rate comparison + AI */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16,
        }}
      >
        <QuotationJobsPendingCard />
        <RateComparisonMini />
        <AiSuggestSplitCard />
      </div>
    </div>
  );
}
